import { useEffect, useMemo, useRef, useState } from 'react';
import { get, onValue, ref } from 'firebase/database';
import { database } from '../firebase';
import { useAuth } from '../contexts/AuthContext';
import {
  COMMUNITY_FEATURE_SESSION_EVENT,
  DEFAULT_COLLABORATOR_PERMISSIONS,
  OWNER_PERMISSIONS,
  hasDepartmentAccess,
  normalizeCommunityChannels,
  normalizeMemberRecord,
  normalizePermissionState,
  readCommunityFeatureSessionCache,
  writeTeamAccessProfileStorageCache,
  writeCommunityFeatureSessionCache
} from '../utils/teamAccess';

const getTeamProfileKey = (email = '') =>
  String(email || '')
    .trim()
    .toLowerCase()
    .replace(/[.#$/[\]]/g, '_');

const getTeamProfileLookupKeys = ({ uid = '', email = '', fallbackId = '' } = {}) => {
  const keys = [];
  const primaryUid = String(uid || fallbackId || '').trim();
  const normalizedEmail = String(email || '').trim().toLowerCase();

  if (primaryUid) {
    keys.push(primaryUid);
  }

  if (normalizedEmail) {
    const legacyEmailKey = getTeamProfileKey(normalizedEmail);
    if (!keys.includes(legacyEmailKey)) {
      keys.push(legacyEmailKey);
    }
  }

  return keys;
};

const DENIED_PERMISSIONS = Object.freeze(Object.fromEntries(
  Object.keys(DEFAULT_COLLABORATOR_PERMISSIONS).map((key) => [key, false])
));

const buildDefaultAccessProfile = (currentUser = null) =>
  normalizeMemberRecord({
    uid: currentUser?.uid || '',
    email: currentUser?.email || '',
    name: currentUser?.displayName || '',
    permissions: DENIED_PERMISSIONS
  });

const isActiveAccessRecord = (value) => {
  if (!value || typeof value !== 'object') return false;
  const status = String(
    value.accessStatus || value.access_status || value.status || ''
  ).trim().toLowerCase();
  if (['inactive', 'disabled', 'revoked', 'removed', 'rejected', 'expired'].includes(status)) return false;
  return value.active !== false && value.disabled !== true && value.revoked !== true;
};

export default function useTeamAccessProfile(companyUIDOverride = '') {
  const { currentUser } = useAuth();
  // Local storage is a display cache, never an authorization source. Starting
  // unresolved prevents a revoked teammate from opening a direct RTDB listener
  // while the fresh collaborator/profile records are still loading.
  const initialAccessState = null;
  const [companyUID, setCompanyUID] = useState(initialAccessState?.companyUID || companyUIDOverride || '');
  const companyUIDRef = useRef(initialAccessState?.companyUID || companyUIDOverride || '');
  const [isCompanyOwner, setIsCompanyOwner] = useState(initialAccessState?.isCompanyOwner === true);
  const [loading, setLoading] = useState(() => !initialAccessState);
  const [companyFeatures, setCompanyFeatures] = useState(() => ({
    communityEnabled: readCommunityFeatureSessionCache(currentUser?.uid, companyUIDOverride || '') ?? null
  }));
  const [accessProfile, setAccessProfile] = useState(() => initialAccessState?.accessProfile || buildDefaultAccessProfile(currentUser));

  useEffect(() => {
    companyUIDRef.current = companyUID;
  }, [companyUID]);

  useEffect(() => {
    if (!currentUser) {
      setCompanyUID('');
      setIsCompanyOwner(false);
      setCompanyFeatures({ communityEnabled: null });
      setAccessProfile(normalizeMemberRecord({ permissions: DENIED_PERMISSIONS }));
      setLoading(false);
      return undefined;
    }

    let cancelled = false;
    let collaboratorUnsubscribe;
    let profileUnsubscribes = [];
    // Re-resolve authorization from live Firebase records on every mount. Cached
    // profiles remain useful to other display-only consumers, but not this gate.
    const cachedAccessState = null;

    const applyAccessState = (nextState = {}) => {
      if (cancelled) return;

      const nextCompanyUID = String(nextState.companyUID || companyUIDOverride || '').trim();
      setCompanyUID(nextCompanyUID);
      companyUIDRef.current = nextCompanyUID;
      setIsCompanyOwner(nextState.isCompanyOwner === true);
      setAccessProfile(nextState.accessProfile || buildDefaultAccessProfile(currentUser));
      setLoading(false);
    };

    if (cachedAccessState) {
      applyAccessState(cachedAccessState);
    } else {
      setCompanyUID(companyUIDOverride || '');
      companyUIDRef.current = companyUIDOverride || '';
      setIsCompanyOwner(false);
      setAccessProfile(buildDefaultAccessProfile(currentUser));
      setLoading(true);
    }

    const handleCommunityFeatureSessionChange = (event) => {
      const eventUserId = String(event?.detail?.userId || '').trim();
      const eventCompanyUID = String(event?.detail?.companyUID || '').trim();
      const knownCompanyUID = String(companyUIDOverride || companyUIDRef.current || '').trim();

      if (eventUserId !== String(currentUser.uid || '').trim()) {
        return;
      }

      if (knownCompanyUID && eventCompanyUID && eventCompanyUID !== knownCompanyUID) {
        return;
      }

      if (typeof event?.detail?.communityEnabled === 'boolean') {
        setCompanyFeatures({ communityEnabled: event.detail.communityEnabled });
        return;
      }

      setCompanyFeatures({ communityEnabled: null });
    };

    window.addEventListener(COMMUNITY_FEATURE_SESSION_EVENT, handleCommunityFeatureSessionChange);

    const resolveCompanyFeatures = async (resolvedCompanyUID = '') => {
      if (!resolvedCompanyUID) {
        setCompanyFeatures({ communityEnabled: null });
        return;
      }

      const cachedValue = readCommunityFeatureSessionCache(currentUser.uid, resolvedCompanyUID);
      if (typeof cachedValue === 'boolean') {
        setCompanyFeatures({ communityEnabled: cachedValue });
        return;
      }

      try {
        const snapshot = await get(ref(database, `companies/${resolvedCompanyUID}/settings/customFeatures`));
        if (cancelled) return;

        const data = snapshot.exists() ? (snapshot.val() || {}) : {};
        const communityEnabled = data.community !== false;
        writeCommunityFeatureSessionCache(currentUser.uid, resolvedCompanyUID, communityEnabled);
        setCompanyFeatures({ communityEnabled });
      } catch (error) {
        console.error('Error loading company feature settings:', error);
        if (!cancelled) {
          setCompanyFeatures({ communityEnabled: null });
        }
      }
    };

    const resolveAccess = async () => {
      if (!cachedAccessState) {
        setLoading(true);
      }

      try {
        const ownerSnap = await get(ref(database, `companies/${currentUser.uid}`));
        if (cancelled) return;

        if (ownerSnap.exists()) {
          const ownerProfile = normalizeMemberRecord({
            uid: currentUser.uid,
            email: currentUser.email || '',
            name: currentUser.displayName || '',
            permissions: OWNER_PERMISSIONS,
            departmentScope: 'all',
            departments: []
          });
          writeTeamAccessProfileStorageCache(currentUser.uid, currentUser.uid, {
            companyUID: currentUser.uid,
            isCompanyOwner: true,
            accessProfile: ownerProfile
          });
          setCompanyUID(currentUser.uid);
          companyUIDRef.current = currentUser.uid;
          setIsCompanyOwner(true);
          await resolveCompanyFeatures(currentUser.uid);
          setAccessProfile(ownerProfile);
          setLoading(false);
          return;
        }

        const collaboratorRef = ref(database, `collaborators/${currentUser.uid}`);
        const collaboratorSnap = await get(collaboratorRef);
        if (cancelled) return;

        if (!collaboratorSnap.exists()) {
          const deniedProfile = buildDefaultAccessProfile(currentUser);
          writeTeamAccessProfileStorageCache(currentUser.uid, companyUIDOverride || '', {
            companyUID: companyUIDOverride || '',
            isCompanyOwner: false,
            accessProfile: deniedProfile
          });
          setCompanyUID(companyUIDOverride || '');
          companyUIDRef.current = companyUIDOverride || '';
          setIsCompanyOwner(false);
          setCompanyFeatures({ communityEnabled: null });
          setAccessProfile(deniedProfile);
          setLoading(false);
          return;
        }

        const initialCollaboratorData = collaboratorSnap.val() || {};
        if (!isActiveAccessRecord(initialCollaboratorData)) {
          writeTeamAccessProfileStorageCache(currentUser.uid, companyUIDOverride || '', null);
          applyAccessState({
            companyUID: companyUIDOverride || '',
            isCompanyOwner: false,
            accessProfile: buildDefaultAccessProfile(currentUser)
          });
          return;
        }
        const resolvedCompanyUID = companyUIDOverride || initialCollaboratorData.companyId || initialCollaboratorData.company_uid || '';
        const fallbackEmail = initialCollaboratorData.email || currentUser.email || '';
        await resolveCompanyFeatures(resolvedCompanyUID);
        const teamProfileKeys = getTeamProfileLookupKeys({
          uid: currentUser.uid,
          // A legacy email-key profile can enrich permissions only after a
          // verified, accepted UID-bound collaborator has the same email.
          email: currentUser.emailVerified
            && String(initialCollaboratorData.email || '').trim().toLowerCase()
              === String(currentUser.email || '').trim().toLowerCase()
            ? fallbackEmail
            : ''
        });

        let latestCollaboratorData = initialCollaboratorData;
        let latestProfileEntries = {};
        const applyLatestState = () => {
          if (cancelled) return;

          const collaboratorCompanyUID = latestCollaboratorData?.companyId || latestCollaboratorData?.company_uid || '';
          if (!isActiveAccessRecord(latestCollaboratorData) || collaboratorCompanyUID !== resolvedCompanyUID) {
            writeTeamAccessProfileStorageCache(currentUser.uid, resolvedCompanyUID, null);
            setCompanyUID('');
            companyUIDRef.current = '';
            setIsCompanyOwner(false);
            setCompanyFeatures({ communityEnabled: null });
            setAccessProfile(buildDefaultAccessProfile(currentUser));
            setLoading(false);
            return;
          }

          const uidEntry = latestProfileEntries[currentUser.uid];
          // An explicit UID-scoped revocation wins over collaborator and legacy
          // email records, matching backend authorization.
          if (uidEntry?.exists && !isActiveAccessRecord(uidEntry.value)) {
            writeTeamAccessProfileStorageCache(currentUser.uid, resolvedCompanyUID, null);
            setCompanyUID('');
            companyUIDRef.current = '';
            setIsCompanyOwner(false);
            setCompanyFeatures({ communityEnabled: null });
            setAccessProfile(buildDefaultAccessProfile(currentUser));
            setLoading(false);
            return;
          }

          const prioritizedProfileData = teamProfileKeys.reduce((selectedProfile, profileKey) => {
            if (selectedProfile) {
              return selectedProfile;
            }

            const profileEntry = latestProfileEntries[profileKey];
            if (!profileEntry?.exists || !isActiveAccessRecord(profileEntry.value)) return null;
            if (profileKey !== currentUser.uid) {
              const profileUID = String(profileEntry.value?.uid || '');
              const profileEmail = String(profileEntry.value?.email || '').trim().toLowerCase();
              if (profileUID !== currentUser.uid || profileEmail !== String(currentUser.email || '').trim().toLowerCase()) {
                return null;
              }
            }
            return profileEntry.value;
          }, null) || {};

          const nextProfile = normalizeMemberRecord({
            uid: currentUser.uid,
            email: fallbackEmail,
            name: currentUser.displayName || '',
            ...latestCollaboratorData,
            ...prioritizedProfileData
          });

          writeTeamAccessProfileStorageCache(currentUser.uid, resolvedCompanyUID, {
            companyUID: resolvedCompanyUID,
            isCompanyOwner: false,
            accessProfile: nextProfile
          });
          setCompanyUID(resolvedCompanyUID);
          companyUIDRef.current = resolvedCompanyUID;
          setIsCompanyOwner(false);
          setAccessProfile(nextProfile);
          setLoading(false);
        };

        collaboratorUnsubscribe = onValue(collaboratorRef, (snapshot) => {
          latestCollaboratorData = snapshot.exists() ? (snapshot.val() || {}) : {};
          applyLatestState();
        });

        if (resolvedCompanyUID && teamProfileKeys.length > 0) {
          teamProfileKeys.forEach((profileKey) => {
            const teamProfileRef = ref(database, `companies/${resolvedCompanyUID}/teamAccessProfiles/${profileKey}`);
            const unsubscribe = onValue(teamProfileRef, (snapshot) => {
              latestProfileEntries = {
                ...latestProfileEntries,
                [profileKey]: {
                  exists: snapshot.exists(),
                  value: snapshot.exists() ? (snapshot.val() || {}) : {}
                }
              };
              applyLatestState();
            });
            profileUnsubscribes.push(unsubscribe);
          });
        }

        applyLatestState();
      } catch (error) {
        console.error('Error resolving team access profile:', error);
        if (!cancelled && !cachedAccessState) {
          const deniedProfile = buildDefaultAccessProfile(currentUser);
          writeTeamAccessProfileStorageCache(currentUser.uid, companyUIDOverride || '', {
            companyUID: companyUIDOverride || '',
            isCompanyOwner: false,
            accessProfile: deniedProfile
          });
          setCompanyUID(companyUIDOverride || '');
          companyUIDRef.current = companyUIDOverride || '';
          setIsCompanyOwner(false);
          setCompanyFeatures({ communityEnabled: null });
          setAccessProfile(deniedProfile);
          setLoading(false);
        }
      }
    };

    resolveAccess();

    return () => {
      cancelled = true;
      window.removeEventListener(COMMUNITY_FEATURE_SESSION_EVENT, handleCommunityFeatureSessionChange);
      if (collaboratorUnsubscribe) collaboratorUnsubscribe();
      profileUnsubscribes.forEach((unsubscribe) => unsubscribe());
    };
  }, [companyUIDOverride, currentUser]);

  const userPermissions = useMemo(
    () => normalizePermissionState(
      accessProfile.permissions || (isCompanyOwner ? OWNER_PERMISSIONS : DEFAULT_COLLABORATOR_PERMISSIONS)
    ),
    [accessProfile.permissions, isCompanyOwner]
  );

  const allowedDepartments = useMemo(
    () => (Array.isArray(accessProfile.departments) ? accessProfile.departments : []),
    [accessProfile.departments]
  );
  const allowedCommunityChannels = useMemo(
    () => (
      isCompanyOwner
        ? ['talent_pool', 'workforce']
        : normalizeCommunityChannels(accessProfile.communityChannels || [])
    ),
    [accessProfile.communityChannels, isCompanyOwner]
  );
  const departmentScope = accessProfile.departmentScope || 'all';

  const canAccessDepartment = useMemo(
    () => (department) => hasDepartmentAccess({ departmentScope, departments: allowedDepartments }, department),
    [allowedDepartments, departmentScope]
  );

  const filterByDepartmentAccess = useMemo(
    () => (items = [], getDepartment = (item) => item?.department) =>
      (Array.isArray(items) ? items : []).filter((item) => canAccessDepartment(getDepartment(item))),
    [canAccessDepartment]
  );

  const hasPermission = useMemo(
    () => (permissionKey) => isCompanyOwner || userPermissions[permissionKey] === true,
    [isCompanyOwner, userPermissions]
  );

  return {
    accessProfile,
    allowedDepartments,
    allowedCommunityChannels,
    canAccessDepartment,
    companyFeatures,
    companyUID,
    departmentScope,
    filterByDepartmentAccess,
    hasPermission,
    isCompanyOwner,
    isScoped: departmentScope === 'specific' && allowedDepartments.length > 0,
    loading,
    userPermissions
  };
}
